import { useState } from "react";
import type { CarrierDto } from "@babackapp/private-api-client/types";
import { fetchClient } from "@babackapp/private-api-client/generated/client";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { useCarriersContext } from "../../use-carriers-context";
import { serviceLaneFor } from "../fedex-plans";
import { RatesTable, type RateDetail } from "../forms/fedex-rates-display";

type RateResponse = {
  transactionId?: string;
  output?: { rateReplyDetails?: RateDetail[] };
  errors?: { code: string; message: string }[];
};

function extractApiMessage(m: unknown): string | undefined {
  if (typeof m === "string") return m;
  if (Array.isArray(m)) {
    const parts = m
      .map((x) =>
        typeof x === "string"
          ? x
          : x && typeof x === "object" && "message" in x
            ? String((x as { message: unknown }).message)
            : "",
      )
      .filter(Boolean);
    return parts.length ? parts.join(", ") : undefined;
  }
  return undefined;
}

// Self-service FedEx return-rate viewer, embedded inline in the carrier edit
// modal. Origin = where the return ships from (the customer); destination = where
// it ships to (the return warehouse). Both are free, editable fields: FedEx
// domestic products are US-only, and a shop's warehouse may sit in any country
// (or a shop may have several), so locking the destination to one warehouse made
// domestic lanes unreachable (e.g. FR→FR → RATE.LOCATION.NOSERVICE). Leaving both
// ends editable lets the merchant quote any real route. No serviceType is sent,
// so FedEx returns every product applicable to whatever route is entered. Since
// this is a returns product there is no DDP/DAP choice. Pickup type is not sent:
// it does not change the FedEx rate quote (any scheduled-pickup fee is billed
// separately). Fields are pre-filled from the selected product's lane and the
// shop's default warehouse as a convenience.
export function FedexRatesPanel({
  carrier,
  selectedServiceValue,
}: {
  carrier: CarrierDto;
  selectedServiceValue: string;
}) {
  const t = useTranslation();
  const { shop } = useCarriersContext();
  const shopId = shop.id;

  const defaultLocation =
    shop.locations.find((l) => l.isDefault) ?? shop.locations[0];
  const warehouseCountry =
    defaultLocation?.address?.country_code ??
    carrier.destinationAccepted[0] ??
    "FR";
  const warehousePostal = defaultLocation?.address?.zip ?? "";

  // Pre-fill from the configured product's lane: a domestic/One Rate product is
  // US-only (US→US); an international product ships from abroad to the warehouse.
  const initialLane = serviceLaneFor(selectedServiceValue);
  const prefill =
    initialLane === "international"
      ? {
          originCountry:
            carrier.originAccepted.find((c) => c !== warehouseCountry) ?? "US",
          originPostal: "",
          destCountry: warehouseCountry,
          destPostal: warehousePostal,
        }
      : { originCountry: "US", originPostal: "", destCountry: "US", destPostal: "" };

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RateResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [originCountry, setOriginCountry] = useState(prefill.originCountry);
  const [originPostal, setOriginPostal] = useState(prefill.originPostal);
  const [destCountry, setDestCountry] = useState(prefill.destCountry);
  const [destPostal, setDestPostal] = useState(prefill.destPostal);
  const [weightValue, setWeightValue] = useState("1");
  const [weightUnit, setWeightUnit] = useState<"KG" | "LB">("KG");

  const isInternational =
    originCountry.trim().toUpperCase() !== destCountry.trim().toUpperCase();
  // FedEx rates a domestic parcel by origin-ZIP → destination-ZIP zone, so both
  // postal codes are required; an international quote is driven by the countries.
  const needsPostal = !isInternational;
  const hasCountries =
    originCountry.trim().length === 2 && destCountry.trim().length === 2;
  const hasPostals =
    originPostal.trim() !== "" && destPostal.trim() !== "";
  const canQuote = hasCountries && (!needsPostal || hasPostals);

  // One rate call. `serviceType` omitted → FedEx returns every applicable
  // product; passed explicitly → just that product (used to pull in Ground).
  const fetchRates = async (
    serviceType?: string,
  ): Promise<RateResponse> => {
    const body: Record<string, unknown> = {
      carrierId: carrier.id,
      origin: {
        postalCode: originPostal,
        countryCode: originCountry.trim().toUpperCase(),
      },
      destination: {
        postalCode: destPostal,
        countryCode: destCountry.trim().toUpperCase(),
      },
      weight: {
        value: Number(weightValue) || 1,
        units: weightUnit,
      },
    };
    if (serviceType) body.serviceType = serviceType;

    const { data, error: err, response } = await fetchClient.POST(
      `/shops/${shopId}/carriers/fedex/rates` as never,
      { body } as never,
    );
    if (err || !response.ok) {
      const e = err as
        | { message?: unknown; errors?: { message?: string }[] }
        | undefined;
      const msg = extractApiMessage(e?.message) ?? e?.errors?.[0]?.message;
      throw new Error(msg || `HTTP ${response.status}`);
    }
    return data as RateResponse;
  };

  const detailsOf = (r: RateResponse) => r.output?.rateReplyDetails ?? [];

  // Ground/Home Delivery are excluded from the "all services" response, so on a
  // domestic lane we fetch them explicitly and merge. Ground to a residential
  // recipient is rejected in favour of Home Delivery, so fall back to it. Purely
  // best-effort: a failed call (incl. FedEx Ground rating being down) is ignored.
  const fetchGroundDetails = async (): Promise<RateDetail[]> => {
    for (const service of ["FEDEX_GROUND", "GROUND_HOME_DELIVERY"]) {
      try {
        const res = await fetchRates(service);
        const details = detailsOf(res);
        if (details.length) return details;
      } catch {
        // try the next Ground variant / give up
      }
    }
    return [];
  };

  const handleGetRates = async () => {
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      // Base (all Express) and the best-effort Ground lookup run concurrently so
      // the extra domestic call adds no latency.
      const [base, ground] = await Promise.all([
        fetchRates(),
        isInternational ? Promise.resolve<RateDetail[]>([]) : fetchGroundDetails(),
      ]);
      const seen = new Set(detailsOf(base).map((d) => d.serviceType));
      const merged = [
        ...detailsOf(base),
        ...ground.filter((d) => !seen.has(d.serviceType)),
      ];
      setResult({ ...base, output: { rateReplyDetails: merged } });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const apiError = result?.errors?.[0];

  return (
    <div className="space-y-3 border-t border-gray-200 pt-3">
      <Button
        variant="outline"
        size="sm"
        type="button"
        onClick={() => setOpen((v) => !v)}
      >
        {open
          ? t("carrier.fedexCsp.hideRates")
          : t("carrier.fedexCsp.checkRates")}
      </Button>

      {open && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">
                {t("carrier.fedexCsp.origin")}
              </label>
              <Input
                value={originCountry}
                onChange={(e) => setOriginCountry(e.target.value)}
                maxLength={2}
                placeholder="US"
              />
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">
                {t("carrier.fedexCsp.postalCode")}
              </label>
              <Input
                value={originPostal}
                onChange={(e) => setOriginPostal(e.target.value)}
                placeholder="31905"
              />
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">
                {t("carrier.fedexCsp.destination")}
              </label>
              <Input
                value={destCountry}
                onChange={(e) => setDestCountry(e.target.value)}
                maxLength={2}
                placeholder="FR"
              />
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">
                {t("carrier.fedexCsp.postalCode")}
              </label>
              <Input
                value={destPostal}
                onChange={(e) => setDestPostal(e.target.value)}
                placeholder="77200"
              />
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">
                {t("carrier.fedexCsp.weight")}
              </label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="0"
                  step="0.1"
                  value={weightValue}
                  onChange={(e) => setWeightValue(e.target.value)}
                />
                <Select
                  value={weightUnit}
                  onValueChange={(v) => setWeightUnit(v as "KG" | "LB")}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="KG">KG</SelectItem>
                    <SelectItem value="LB">LB</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500">
            {isInternational
              ? t("carrier.fedexCsp.allRatesInternational")
              : t("carrier.fedexCsp.allRatesDomestic")}
          </p>

          {needsPostal && !hasPostals && (
            <p className="text-xs text-gray-500">
              {t("carrier.fedexCsp.domesticPostalRequired")}
            </p>
          )}

          <Button
            size="sm"
            type="button"
            disabled={loading || !canQuote}
            onClick={handleGetRates}
          >
            {t("carrier.fedexCsp.getRates")}
          </Button>

          {loading && (
            <p className="text-sm text-gray-500">
              {t("carrier.fedexCsp.ratesLoading")}
            </p>
          )}

          {!loading && error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}

          {!loading && apiError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
              {apiError.code}: {apiError.message}
            </div>
          )}

          {!loading && result && !apiError && (
            <RatesTable rates={result.output?.rateReplyDetails ?? []} />
          )}
        </div>
      )}
    </div>
  );
}
